<template>
  <v-navigation-drawer
    v-model="drawer"
    :rail="rail"
    permanent
    @click="rail = false"
  >
    <div class="sidebar-header">
      <div class="logo-container" :class="{ 'rail-mode': rail }">
        <div class="logo-image">
          <img src="@/assets/logo.png" alt="Confidence Maturity Logo" />
        </div>
        <div v-if="!rail" class="logo-full">
          <span class="logo-title">Confidence Maturity</span>
          <span class="logo-subtitle">Evaluator</span>
        </div>
        <v-btn
          v-if="!rail"
          variant="text"
          icon="mdi-chevron-left"
          @click.stop="rail = true"
          class="toggle-btn"
        ></v-btn>
      </div>
    </div>

    <div class="project-section px-2 py-1">
      <ProjectSwitcher :rail="rail" />
    </div>

    <v-divider></v-divider>

    <v-list density="compact" nav>
      <v-list-item
        v-for="item in menuItems"
        :key="item.title"
        :value="item.title"
        :to="item.to"
        :prepend-icon="item.icon"
        :title="rail ? '' : item.title"
      ></v-list-item>
    </v-list>

    <!-- Recent Items Section -->
    <template v-if="recentItems.length > 0">
      <v-divider></v-divider>
      <v-list density="compact" nav class="recent-items-list">
        <v-list-subheader :class="{ 'px-2': rail, 'rail-subheader': rail }">
          <v-icon v-if="rail">mdi-history</v-icon>
          <span v-else>Recent Items</span>
        </v-list-subheader>
        <v-list-item
          v-for="item in recentItems"
          :key="item.id"
          :to="getRecentItemRoute(item)"
          class="recent-item"
          :class="{ 'rail-mode': rail }"
        >
          <template #prepend>
            <v-icon v-if="!rail" size="18">
              <template v-if="item.type === 'system'">mdi-cube-outline</template>
              <template v-else-if="item.type === 'subsystem'">mdi-cube-scan</template>
              <template v-else-if="item.type === 'action'">mdi-rocket-launch</template>
              <template v-else>mdi-engine-outline</template>
            </v-icon>
          </template>
          <template #default>
            <div class="recent-item-content">
              <template v-if="rail">
                {{ item.id }}
              </template>
              <template v-else>
                <span class="recent-item-id">{{ item.id }}</span>
                <span class="recent-item-name">{{ item.name }}</span>
              </template>
            </div>
          </template>
        </v-list-item>
      </v-list>
    </template>

    <!-- Add account section at bottom -->
    <template #append>
      <v-divider></v-divider>
      <div class="account-section" :class="{ 'rail-mode': rail }">
        <v-list density="compact" nav>
          <v-list-item
            :title="rail ? '' : userName"
            :subtitle="rail ? '' : userEmail"
            :to="'/profile'"
          >
            <template #prepend>
              <v-avatar color="primary" size="32">
                <span class="monogram-text">{{ userMonogram }}</span>
              </v-avatar>
            </template>
            
            <template #append>
              <v-btn
                v-if="!rail"
                variant="text"
                icon="mdi-logout"
                size="small"
                @click.stop="handleLogout"
              ></v-btn>
            </template>
          </v-list-item>
        </v-list>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { useAuthStore } from '@/stores/auth'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import ProjectSwitcher from '@/components/common/ProjectSwitcher.vue'

export default {
  name: 'Sidebar',
  components: {
    ProjectSwitcher
  },
  
  setup() {
    const auth = useAuthStore()
    const router = useRouter()
    const store = useStore()

    const userMonogram = computed(() => auth.user?.monogram || '??')
    const userName = computed(() => auth.user?.full_name || 'User')
    const userEmail = computed(() => auth.user?.email || '')
    const recentItems = computed(() => store.state.recentItems)
    const currentProject = computed(() => store.getters['projects/currentProject'])
    const projectAcronym = computed(() => currentProject.value?.acronym?.toLowerCase() || 'def')

    const menuItems = computed(() => [
      {
        title: 'Dashboard',
        icon: 'mdi-view-dashboard',
        to: `/${projectAcronym.value}/dashboard`
      },
      {
        title: 'In Flight',
        icon: 'mdi-airplane-takeoff',
        to: `/${projectAcronym.value}/in-flight`
      },
      {
        title: 'Landed',
        icon: 'mdi-airplane-landing',
        to: `/${projectAcronym.value}/landed`
      },
      // Maturity-Confidence temporarily hidden
      {
        title: 'Devitems',
        icon: 'mdi-engine-outline',
        to: `/${projectAcronym.value}/engineering-items`
      },
      {
        title: 'Subsystems',
        icon: 'mdi-cube-scan',
        to: `/${projectAcronym.value}/subsystems`
      },
      {
        title: 'Actions',
        icon: 'mdi-rocket-launch',
        to: `/${projectAcronym.value}/actions`
      },
      {
        title: 'Disciplines',
        icon: 'mdi-office-building-cog',
        to: `/${projectAcronym.value}/systems`
      },
      {
        title: 'Dependencies',
        icon: 'mdi-graph',
        to: `/${projectAcronym.value}/dependencies`
      },
      {
        title: 'Users',
        icon: 'mdi-account-group',
        to: `/${projectAcronym.value}/users`
      },
      {
        title: 'Level Models',
        icon: 'mdi-chart-bell-curve',
        to: `/${projectAcronym.value}/level-models`
      },
      {
        title: 'Import',
        icon: 'mdi-upload',
        to: `/${projectAcronym.value}/import`
      }
    ])

    const handleLogout = async () => {
      try {
        await auth.logout()
        router.push('/login')
      } catch (error) {
        console.error('Logout error:', error)
      }
    }

    // Load recent items when component mounts
    store.dispatch('loadRecentItems')

    return {
      userMonogram,
      userName,
      userEmail,
      handleLogout,
      recentItems,
      menuItems,
      projectAcronym
    }
  },

  data: () => ({
    drawer: true,
    rail: true
  }),

  methods: {
    getRecentItemRoute(item) {
      const prefix = `/${this.projectAcronym}`
      switch (item.type) {
        case 'system':
          return `${prefix}/systems/${item.id}/${item.slug}`
        case 'subsystem':
          return `${prefix}/subsystems/${item.id}/${item.slug}`
        case 'action':
          return `${prefix}/actions/${item.id}/${item.slug}`
        case 'devitem':
        default:
          return `${prefix}/devitems/${item.id}/${item.slug}`
      }
    }
  }
}
</script>

<style scoped>
.sidebar-header {
  min-height: 64px;  /* Ensure consistent height */
  padding: 8px;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px;
  height: 48px;
  position: relative;
}

.logo-image {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.logo-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.logo-full {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.logo-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
}

.logo-subtitle {
  font-size: var(--font-size-xs);
  color: var(--color-secondary);
}

.toggle-btn {
  position: absolute;
  right: 4px;
  top: 80%;
  transform: translateY(-50%);
}

.rail-mode {
  justify-content: center;
}

.rail-mode .logo-image {
  width: 32px;
  height: 32px;
}

.rail-mode .logo-full {
  display: none;
}

.account-section {
  padding: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.account-section.rail-mode {
  display: flex;
  justify-content: center;
  padding: 4px;
}

.account-section :deep(.v-list-item__prepend) > .v-avatar {
  margin-inline-end: 8px;
}

.account-section :deep(.v-list-item) {
  min-height: 48px;
}

.account-section.rail-mode :deep(.v-list-item) {
  padding: 0;
  min-height: 40px;
}

.monogram-text {
  font-size: 0.875rem; /* 14px */
  font-weight: 500;
  color: white;
  line-height: 1;
}

.rail-mode .monogram-text {
  font-size: 0.75rem; /* 12px */
}

.recent-items-list :deep(.v-list-subheader.rail-subheader) {
  padding-left: 0;
  justify-content: center;
}

.recent-items-list :deep(.v-list-subheader) {
  font-weight: var(--font-weight-bold);
}

.recent-item :deep(.v-list-item__prepend) {
  margin-inline-end: 2px;
}

.recent-item {
  padding-left: 8px;
}

.recent-item :deep(.v-list-item__content) {
  padding-left: 0;
}

.recent-item.rail-mode {
  min-height: 32px;
  padding: 0;
}

.recent-item.rail-mode :deep(.v-list-item__content) {
  padding: 0;
  margin: 0;
  margin-left: -8px;  /* Pull content to the left */
}

.recent-item.rail-mode .recent-item-content {
  font-size: 0.75rem;
  padding: 0;
}

/* Override Vuetify's default padding */
.v-navigation-drawer--rail :deep(.v-list-item) {
  padding-inline: 0;
}

.v-navigation-drawer--rail .recent-item.rail-mode {
  padding: 0;
  margin: 0;
}

.recent-item :deep(.v-list-item__spacer) {
  width: 8px !important;  /* Override Vuetify's default 32px */
}

.recent-item :deep(.v-list-item__content) {
  font-size: var(--font-size-xs);
}

.recent-item-content {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.recent-item-id {
  color: rgb(var(--v-theme-primary));
  min-width: 20px;
}

.recent-item-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style> 